import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { MatDialogRef, MatDialogContent, MatDialogActions, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { TranslatePipe } from '@ngx-translate/core'
import { FileUploadComponent, FileSizeLimitExceededEvent, PageLimitExceededEvent } from '../../../../file-upload/file-upload.component'
import { SentryService } from '../../../../sentry.service'
import { DocumentUploadInfo } from '../../../../utils'

export type BoardPackIngestDialogInputData = {
  callId: number
}

export type BoardPackIngestDialogOutputData = {
  uploadKey: string
  fileName: string
}

@Component({
  selector: 'app-board-pack-ingest-dialog',
  imports: [MatDialogContent, MatDialogActions, MatDialogTitle, MatButton, TranslatePipe, FileUploadComponent],
  templateUrl: './board-pack-ingest-dialog.component.html',
  styleUrl: './board-pack-ingest-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BoardPackIngestDialogComponent {
  dialogRef = inject(MatDialogRef<BoardPackIngestDialogComponent>)
  private data = inject<BoardPackIngestDialogInputData>(MAT_DIALOG_DATA)
  private sentryService = inject(SentryService)

  readonly MAX_BOARD_PACK_PAGES = 550
  readonly MAX_BOARD_PACK_FILE_SIZE_BYTES = 20_000_000

  uploadInfo: DocumentUploadInfo | null = null

  onFileChange(info: DocumentUploadInfo | null): void {
    this.uploadInfo = info
  }

  onPageLimitExceeded(event: PageLimitExceededEvent): void {
    this.reportLimitExceeded(`Board pack exceeds maximum page count for ingestion for call ${this.data.callId}`, {
      pageCount: event.pageCount,
      maxPages: event.maxPages,
    })
  }

  onFileSizeLimitExceeded(event: FileSizeLimitExceededEvent): void {
    this.reportLimitExceeded(`Board pack exceeds maximum file size for ingestion for call ${this.data.callId}`, {
      fileSize: event.fileSize,
      maxFileSize: event.maxFileSize,
    })
  }

  private reportLimitExceeded(message: string, extra: Record<string, number>): void {
    this.sentryService.captureMessage(message, {
      level: 'error',
      tags: { feature: 'board-pack-ingestion' },
      extra,
    })
  }

  submit(): void {
    if (this.uploadInfo === null) {
      return
    }
    const result: BoardPackIngestDialogOutputData = {
      uploadKey: this.uploadInfo.key,
      fileName: this.uploadInfo.fileName,
    }
    this.dialogRef.close(result)
  }

  cancel(): void {
    this.dialogRef.close()
  }

  get isSubmitDisabled(): boolean {
    return this.uploadInfo === null
  }
}
